import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, ZoomIn, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { productService } from '../../services/productService';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';
import { useStore } from '../../hooks/useStore';
import { useToast } from '../../contexts/ToastContext';
import Container from '../../components/layout/Container';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import QuantityStepper from '../../components/common/QuantityStepper';
import Badge from '../../components/common/Badge';
import Skeleton from '../../components/common/Skeleton';
import EmptyState from '../../components/common/EmptyState';
import Breadcrumb from '../../components/common/Breadcrumb';
import LeadInterestModal from '../../components/product/LeadInterestModal';
import RatingStars from '../../components/catalog/RatingStars';
import ProductCarousel from '../../components/catalog/ProductCarousel';
import { formatPrice, hasResellerPricing } from '../../utils/priceCalculator';
import { formatVariationLabel } from '../../utils/variationLabel';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, getTotalItems } = useCart();
  const { isAuthenticated } = useAuth();
  const { isCatalogStore, store } = useStore();
  const { success, error, warning } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [mainImageIdx, setMainImageIdx] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const [selectedVariations, setSelectedVariations] = useState([]);
  const [showPreCheckout, setShowPreCheckout] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const intervalRef = useRef(null);

  // RESELLER "vê" preços de revenda quando o backend serializa resellerPrice
  // (só pra RESELLER em store reseller — serializeProductForRole). A presença
  // do dado é o gate de UI, mais robusto que depender do currentStoreId.
  const isResellerView = !!product?.variations?.some(hasResellerPricing);

  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      const data = await productService.getById(id);
      setProduct(data);
      setMainImageIdx(0);
    } catch (err) {
      error(err.message || 'Erro ao carregar produto');
    } finally {
      setLoading(false);
    }
  }, [id, error]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  // Recomendações: produtos da mesma categoria, ordenados por mais vendidos.
  // Se sem categoria ou categoria pequena, cai pra geral.
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    productService
      .getAll({ categoryId: product.categoryId || undefined, sort: 'bestSelling', limit: 8 })
      .then((data) => {
        if (cancelled) return;
        const filtered = (data?.rows || []).filter((p) => p.id !== product.id);
        setRelated(filtered);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [product]);

  // Auto-rotate da galeria a cada 5s. Pausa enquanto zoom está aberto ou
  // usuário interagiu com thumb (controlado por autoRotate flag).
  useEffect(() => {
    if (!product || zoomOpen || !autoRotate) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return undefined;
    }
    const imgs = Array.isArray(product.images) ? product.images : [];
    if (imgs.length <= 1) return undefined;
    intervalRef.current = setInterval(() => {
      setMainImageIdx((idx) => (idx + 1) % imgs.length);
    }, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [product, zoomOpen, autoRotate]);

  const toggleVariation = (variation) => {
    setSelectedVariations((prev) => {
      const exists = prev.find((i) => i.variation.id === variation.id);
      if (exists) return prev.filter((i) => i.variation.id !== variation.id);
      return [...prev, { variation, quantity: 1 }];
    });
  };

  const updateVariationQty = (variationId, qty) => {
    setSelectedVariations((prev) =>
      prev.map((i) => {
        if (i.variation.id !== variationId) return i;
        const max = i.variation.stock ? parseInt(i.variation.stock, 10) : 999;
        return { ...i, quantity: Math.max(1, Math.min(max, qty)) };
      })
    );
  };

  const removeVariation = (variationId) => {
    setSelectedVariations((prev) => prev.filter((i) => i.variation.id !== variationId));
  };

  // Total de venda (preço cheio do consumidor) — mostrado a todos os papéis
  // como referência. RESELLER vê adicionalmente "Total à pagar" usando o
  // preço de revenda (linha separada, sem ofuscar a referência).
  const totalConsumer = selectedVariations.reduce(
    (sum, { variation, quantity }) => sum + (Number(variation.price) || 0) * quantity,
    0
  );
  const totalReseller = isResellerView
    ? selectedVariations.reduce((sum, { variation, quantity }) => {
        const eff = variation.resellerPrice != null
          ? Number(variation.resellerPrice)
          : Number(variation.price) || 0;
        return sum + eff * quantity;
      }, 0)
    : null;

  const handleAddToCart = () => {
    if (!selectedVariations.length) {
      warning('Selecione pelo menos uma variação');
      return;
    }
    if (!isAuthenticated) {
      warning('Você precisa estar logado para adicionar à sacola');
      return;
    }
    try {
      const before = getTotalItems();
      selectedVariations.forEach(({ variation, quantity }) =>
        addItem(product, variation, quantity, { isReseller: isResellerView })
      );
      const added = getTotalItems() - before;
      setSelectedVariations([]);
      setShowPreCheckout(false);
      success(`${added} ${added === 1 ? 'item adicionado' : 'itens adicionados'} à sacola`);
    } catch (err) {
      error(err.message || 'Erro ao adicionar à sacola');
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Skeleton variant="block" height={400} />
          <div className="space-y-4">
            <Skeleton variant="text" lines={2} />
            <Skeleton variant="text" lines={4} />
            <Skeleton variant="block" height={120} />
          </div>
        </div>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <EmptyState title="Produto não encontrado" description="O produto pode ter sido removido." />
      </Container>
    );
  }

  // Defense: product.images pode vir como string JSON em cenários
  // legados (MariaDB sem JSON parse). Normaliza pra array aqui.
  const rawImages = product.images;
  const parsedImages =
    typeof rawImages === 'string'
      ? (() => { try { return JSON.parse(rawImages); } catch { return []; } })()
      : rawImages;
  const images = Array.isArray(parsedImages) && parsedImages.length ? parsedImages : [null];
  const mainImage = images[mainImageIdx];

  const goPrev = () => {
    setAutoRotate(false);
    setMainImageIdx((idx) => (idx - 1 + images.length) % images.length);
  };
  const goNext = () => {
    setAutoRotate(false);
    setMainImageIdx((idx) => (idx + 1) % images.length);
  };

  const catalogPath = store?.slug ? `/fornecedor/${store.slug}/catalogo` : '/catalogo';
  const homePath = store?.slug ? `/fornecedor/${store.slug}` : '/';

  // Preço cheio sempre como referência. RESELLER ganha label adicional de
  // revenda abaixo (sem riscar/ofuscar o cheio).
  const headlineVariation = product.variations?.[0];
  const headlinePrice = headlineVariation ? Number(headlineVariation.price) || 0 : null;
  const headlineResellerPrice = hasResellerPricing(headlineVariation)
    ? Number(headlineVariation.resellerPrice)
    : null;

  return (
    <Container className="py-6">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<ArrowLeft size={16} />}
        onClick={() => navigate(-1)}
        className="mb-3"
      >
        Voltar
      </Button>
      <Breadcrumb
        className="mb-3"
        items={[
          { label: store?.name || 'Loja', to: homePath },
          { label: 'Catálogo', to: catalogPath },
          { label: product.name },
        ]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Galeria */}
        <div>
          <div className="relative aspect-square rounded-lg overflow-hidden bg-app-surface-hover mb-3 group">
            <button
              type="button"
              className="w-full h-full"
              onClick={() => mainImage && setZoomOpen(true)}
              aria-label="Ampliar imagem"
            >
              <ImageWithFallback
                src={mainImage}
                alt={product.name}
                fallbackText={product.name}
                className="w-full h-full object-cover transition-all duration-300 group-hover:object-contain group-hover:bg-white"
              />
            </button>
            {mainImage && (
              <span className="absolute top-2 right-2 bg-black/50 text-white p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <ZoomIn size={16} />
              </span>
            )}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute top-1/2 left-2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Imagem anterior"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute top-1/2 right-2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Próxima imagem"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => { setAutoRotate(false); setMainImageIdx(idx); }}
                  className={`aspect-square rounded-lg overflow-hidden bg-app-surface-hover ring-2 transition-all ${
                    mainImageIdx === idx ? 'ring-primary' : 'ring-transparent hover:ring-app-border'
                  }`}
                  aria-label={`Imagem ${idx + 1}`}
                >
                  <ImageWithFallback
                    src={img}
                    alt={`${product.name} ${idx + 1}`}
                    fallbackText={`${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detalhes */}
        <div>
          {product.brand && (
            <p className="text-xs font-medium text-app-text-secondary uppercase tracking-wider mb-1">
              {product.brand}
            </p>
          )}
          <h1 className="text-3xl font-extrabold text-app-text mb-2">{product.name}</h1>

          <div className="flex items-center gap-3 mb-4">
            <RatingStars
              rating={product.averageRating}
              count={product.ratingsCount}
              size={14}
            />
            {product.salesCount > 0 && (
              <span className="text-xs text-app-text-secondary">
                {product.salesCount} vendidos
              </span>
            )}
          </div>

          {headlinePrice != null && (
            <div className="mb-4">
              <p className="text-3xl font-extrabold text-app-text leading-none">
                {formatPrice(headlinePrice)}
              </p>
              {headlineResellerPrice != null && (
                <p className="text-sm font-semibold text-success mt-1">
                  Pague apenas: {formatPrice(headlineResellerPrice)}
                </p>
              )}
            </div>
          )}

          {product.description && (
            <p className="text-app-text-secondary mb-4">{product.description}</p>
          )}

          <div className="flex gap-2 text-xs text-app-text-secondary mb-6">
            {product.category && <Badge color="neutral" variant="soft">{product.category}</Badge>}
            {product.department && <Badge color="neutral" variant="soft">{product.department}</Badge>}
          </div>

          {/* Variações */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-app-text mb-3">Variações</h3>
            {product.variations && product.variations.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {product.variations.map((variation) => {
                  const selected = selectedVariations.some((i) => i.variation.id === variation.id);
                  const outOfStock = variation.stock === 0 || variation.stock === '0';
                  return (
                    <button
                      key={variation.id}
                      type="button"
                      onClick={() => !outOfStock && toggleVariation(variation)}
                      disabled={outOfStock}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        outOfStock
                          ? 'border-app-border bg-app-surface opacity-60 cursor-not-allowed'
                          : selected
                          ? 'border-primary bg-primary/5'
                          : 'border-app-border hover:border-primary/50'
                      }`}
                    >
                      <div className="text-sm font-medium text-app-text">
                        {formatVariationLabel(variation)}
                      </div>
                      <div className="text-xs text-app-text-secondary mt-1">
                        {formatPrice(variation.price)}
                      </div>
                      {hasResellerPricing(variation) && (
                        <div className="text-xs font-semibold text-success mt-0.5">
                          Pague apenas: {formatPrice(variation.resellerPrice)}
                        </div>
                      )}
                      {outOfStock && (
                        <Badge color="error" size="sm" variant="soft" className="mt-2">
                          Sem estoque
                        </Badge>
                      )}
                      {!outOfStock && variation.stock != null && (
                        Number(variation.stock) === 1 ? (
                          <Badge color="warning" size="sm" variant="soft" className="mt-2">
                            Última peça!
                          </Badge>
                        ) : Number(variation.stock) <= 5 ? (
                          <Badge color="warning" size="sm" variant="soft" className="mt-2">
                            Poucas unidades
                          </Badge>
                        ) : (
                          <p className="text-xs text-app-text-secondary mt-2">
                            Em estoque
                          </p>
                        )
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-app-text-secondary">Nenhuma variação disponível.</p>
            )}

            {selectedVariations.length > 0 && (
              <div className="mt-4 space-y-2 p-4 bg-app-surface rounded-lg">
                <p className="text-sm font-semibold text-app-text mb-2">
                  Selecionadas ({selectedVariations.length})
                </p>
                {selectedVariations.map(({ variation, quantity }) => (
                  <div
                    key={variation.id}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-app-border"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-app-text truncate">
                        {formatVariationLabel(variation)}
                      </p>
                      <p className="text-xs text-app-text-secondary">
                        {formatPrice((Number(variation.price) || 0) * quantity)}
                      </p>
                    </div>
                    <QuantityStepper
                      value={quantity}
                      onChange={(v) => updateVariationQty(variation.id, v)}
                      max={variation.stock ? parseInt(variation.stock, 10) : undefined}
                      size="sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeVariation(variation.id)}
                      className="p-1.5 text-app-text-secondary hover:text-error transition-colors"
                      aria-label="Remover"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <div className="pt-2 border-t border-app-border space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-app-text-secondary">Total:</span>
                    <span className="text-lg font-bold text-app-text">{formatPrice(totalConsumer)}</span>
                  </div>
                  {isResellerView && totalReseller != null && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-success">Total à pagar:</span>
                      <span className="text-lg font-bold text-success">{formatPrice(totalReseller)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          {isCatalogStore ? (
            <Button
              fullWidth
              size="lg"
              onClick={() => {
                if (!selectedVariations.length) {
                  warning('Selecione pelo menos uma variação');
                  return;
                }
                setShowLeadModal(true);
              }}
              disabled={!selectedVariations.length}
            >
              Tenho interesse (WhatsApp)
            </Button>
          ) : (
            <Button
              fullWidth
              size="lg"
              onClick={() => {
                if (!selectedVariations.length) {
                  warning('Selecione pelo menos uma variação');
                  return;
                }
                setShowPreCheckout(true);
              }}
              disabled={!selectedVariations.length}
            >
              Adicionar à sacola
            </Button>
          )}
        </div>
      </div>

      {/* Avaliações — placeholder até o módulo de reviews entrar. Mostra
          a média atual quando há contagem; senão, convida a comprar. */}
      <section className="my-10 border-t border-app-border pt-8">
        <h2 className="text-xl font-bold text-app-text mb-3">Avaliações</h2>
        {product.ratingsCount > 0 ? (
          <div className="flex items-center gap-4 bg-app-surface rounded-lg p-4">
            <div className="text-center">
              <p className="text-4xl font-extrabold text-app-text leading-none">
                {Number(product.averageRating).toFixed(1)}
              </p>
              <RatingStars
                rating={product.averageRating}
                count={product.ratingsCount}
                hideCount
                size={14}
              />
              <p className="text-xs text-app-text-secondary mt-1">
                {product.ratingsCount} avaliação{product.ratingsCount === 1 ? '' : 'ões'}
              </p>
            </div>
            <p className="text-sm text-app-text-secondary flex-1">
              Confira o que outros compradores acharam deste produto.
            </p>
          </div>
        ) : (
          <p className="text-sm text-app-text-secondary">
            Esse produto ainda não tem avaliações. Seja o primeiro a avaliar depois da compra.
          </p>
        )}
      </section>

      {related.length > 0 && (
        <ProductCarousel
          title="Você também pode gostar"
          products={related}
          seeAllHref={catalogPath}
        />
      )}

      <LeadInterestModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        items={selectedVariations}
        onSuccess={() => {
          success('Mensagem enviada — redirecionando pro WhatsApp da loja');
          setSelectedVariations([]);
        }}
      />

      {/* Zoom modal — imagem em tela cheia com navegação. */}
      {zoomOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setZoomOpen(false)}
          role="dialog"
          aria-label="Imagem ampliada"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            aria-label="Fechar zoom"
          >
            <X size={28} />
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 text-white hover:text-gray-300"
                aria-label="Anterior"
              >
                <ChevronLeft size={36} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 text-white hover:text-gray-300"
                aria-label="Próxima"
              >
                <ChevronRight size={36} />
              </button>
            </>
          )}
          <img
            src={mainImage}
            alt={product.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Pré-checkout */}
      <Modal
        isOpen={showPreCheckout}
        onClose={() => setShowPreCheckout(false)}
        title="Conferir itens"
        size="sm"
        footer={
          <>
            <Button variant="subtle" onClick={() => setShowPreCheckout(false)}>
              Voltar
            </Button>
            <Button onClick={handleAddToCart}>Adicionar à sacola</Button>
          </>
        }
      >
        <div className="space-y-3">
          {selectedVariations.map(({ variation, quantity }) => {
            const unit = Number(variation.price) || 0;
            const showReseller = hasResellerPricing(variation);
            const resellerUnit = showReseller ? Number(variation.resellerPrice) : null;
            return (
              <div key={variation.id} className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-app-text truncate">
                    {formatVariationLabel(variation)}
                  </p>
                  <p className="text-xs text-app-text-secondary">
                    {quantity} × {formatPrice(unit)}
                  </p>
                  {showReseller && (
                    <p className="text-xs font-semibold text-success">
                      Pague apenas: {quantity} × {formatPrice(resellerUnit)}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className={showReseller ? 'text-sm text-app-text-secondary line-through' : 'font-semibold text-app-text'}>
                    {formatPrice(unit * quantity)}
                  </p>
                  {showReseller && (
                    <p className="font-semibold text-success">{formatPrice(resellerUnit * quantity)}</p>
                  )}
                </div>
              </div>
            );
          })}
          <div className="pt-3 border-t border-app-border space-y-1">
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span className="text-app-text">{formatPrice(totalConsumer)}</span>
            </div>
            {isResellerView && totalReseller != null && (
              <div className="flex justify-between font-bold">
                <span className="text-success">Total à pagar</span>
                <span className="text-success">{formatPrice(totalReseller)}</span>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </Container>
  );
};

export default ProductDetailPage;
